<template>
  <Modal
    :show="show"
    @close="handleClose"
    :title="title"
    size="md"
    :close-on-backdrop="true"
    :z-index="10100"
  >
    <div class="insufficient-credits-modal">
      <div class="insufficient-credits-modal__header">
        <Icon icon="mdi:wallet-outline" class="insufficient-credits-modal__icon" />
        <h2 class="insufficient-credits-modal__title">{{ title }}</h2>
      </div>

      <div class="insufficient-credits-modal__content">
        <p class="insufficient-credits-modal__text">{{ message }}</p>
      </div>

      <div v-if="showPurchaseHint" class="insufficient-credits-modal__hint">
        <Icon icon="mdi:information-outline" width="14" height="14" />
        Minimum purchase: $20.00
      </div>
    </div>

    <template #footer>
      <Button variant="secondary" @click="handleClose">Maybe Later</Button>
      <Button class="insufficient-credits-modal__cta-btn" @click="handlePurchaseCredits">
        <Icon icon="mdi:credit-card-outline" class="insufficient-credits-modal__btn-icon" />
        {{ ctaLabel }}
      </Button>
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import { useRouter } from "vue-router";
import Modal from "@/components/ui/Modal.vue";
import Button from "@/components/ui/Button.vue";
import { Icon } from "@iconify/vue";
import { useSubscriptionModals } from "@/composables/ui/useSubscriptionModals";

const router = useRouter();
const { insufficientCreditsModal, hideInsufficientCreditsModal } = useSubscriptionModals();

const show = computed(() => insufficientCreditsModal.show);
const message = computed(() => insufficientCreditsModal.message);
const title = computed(() => insufficientCreditsModal.title || "Insufficient Credits");
const ctaLabel = computed(() => insufficientCreditsModal.ctaLabel || "Purchase Credits");
const showPurchaseHint = computed(() => insufficientCreditsModal.showPurchaseHint ?? true);
const ctaPath = computed(
  () => insufficientCreditsModal.ctaPath || "/settings/billing?openPurchase=true"
);

const handleClose = () => {
  hideInsufficientCreditsModal();
};

const handlePurchaseCredits = () => {
  hideInsufficientCreditsModal();
  router.push(ctaPath.value);
};
</script>

<style scoped>
.insufficient-credits-modal {
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 20px 24px 4px;
}

.insufficient-credits-modal__header {
  text-align: center;
}

.insufficient-credits-modal__icon {
  font-size: 42px;
  color: #dc2626;
  margin-bottom: 4px;
}

.insufficient-credits-modal__title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #dc2626;
  margin: 0;
}

.insufficient-credits-modal__content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.insufficient-credits-modal__text {
  font-size: 0.85rem;
  color: #4b5563;
  margin: 0;
  line-height: 1.5;
  text-align: center;
}

.insufficient-credits-modal__hint {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 16px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.8rem;
  color: #6b7280;
}

.insufficient-credits-modal__cta-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background-color: #7c3aed !important;
  border-color: #7c3aed !important;
  color: #ffffff !important;
  font-weight: 500;
}

.insufficient-credits-modal__cta-btn:hover:not(:disabled) {
  background-color: #6d28d9 !important;
  border-color: #6d28d9 !important;
}

.insufficient-credits-modal__btn-icon {
  font-size: 16px;
}

:deep(.ui-modal__footer) {
  padding: 14px 24px 20px;
}
</style>
